/* import __COLOCATED_TEMPLATE__ from './table.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from 'tracked-toolbox';
import { debounce } from '@ember/runloop';
import ENV from 'embercom/config/environment';
import { isPresent } from '@ember/utils';
import { isEqual } from 'underscore';
import {
  ASSIGNMENT_LIMIT_FILTER_TYPE,
  TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE,
  ASSIGNMENT_LIMIT_COMPARATORS,
  TEAM_FILTER_TYPE,
  ROLE_FILTER_TYPE,
  NO_FILTER,
  STATUS_OBJECT,
} from 'embercom/lib/settings/filters/constants';
import TeammatesCsvExporter from 'embercom/lib/teammate-workload/teammates-csv-export';
import storage from 'embercom/vendor/intercom/storage';

const DEFAULT_FILTERS = {
  [ASSIGNMENT_LIMIT_FILTER_TYPE]: {
    type: ASSIGNMENT_LIMIT_FILTER_TYPE,
    comparator: ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue,
    value: undefined,
  },
  [TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE]: {
    type: TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE,
    comparator: ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue,
    value: undefined,
  },
  [TEAM_FILTER_TYPE]: {
    type: TEAM_FILTER_TYPE,
    comparator: undefined,
    value: [],
    selectedInboxIds: [],
  },
  [ROLE_FILTER_TYPE]: {
    type: ROLE_FILTER_TYPE,
    comparator: undefined,
    value: [],
  },
};

const DEFAULT_PAGE_SIZE = 50;
const SORT_STORAGE_KEY = 'teammate-workload-sort-state';
const FILTER_STORAGE_KEY_PREFIX = 'teammate-workload-filter-state';

export default class Table extends Component {
  @service intl;
  @service appService;
  @service store;
  @service csv;

  @tracked selectedTeammates = [];
  @tracked searchTerm = '';
  @tracked normalizedSearchTerm = '';
  @tracked selectedFilters = this._loadFilterState();

  @tracked currentPage = 0;
  @tracked pageSize = DEFAULT_PAGE_SIZE;

  @tracked tableMode = 'SHRINK';
  @tracked sortState = this._loadSortState();
  @tracked statusFilter = [NO_FILTER];

  get columns() {
    let primaryLabel = 'settings.teammate-workload.columns.primary-inboxes';
    let primaryTooltip = this.intl.t('settings.teammate-workload.primary-inboxes.tooltip');
    return [
      {
        valuePath: 'isSelectable',
        type: 'checkbox',
        width: '1%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.name'),
        valuePath: 'name',
        isSortable: true,
        width: '9%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.status'),
        valuePath: 'status',
        isVisible: false,
        width: '12%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.conversation-assignment-limit'),
        valuePath: 'assignmentLimit',
        isSortable: true,
        tooltip: this.intl.t(this.lbaDescription, {
          htmlSafe: true,
        }),
        width: '12%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.ticket-assignment-limit'),
        valuePath: 'ticketAssignmentLimit',
        isSortable: true,
        isVisible: true,
        tooltip: this.intl.t('settings.teammate-workload.ticket-assignment-limit.tooltip'),
        width: '12%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.email-channel-limit'),
        valuePath: 'emailChannelLimit',
        isSortable: true,
        isVisible: this.appService.app.canUseChannelAssignmentLimits,
        tooltip: this.intl.t('settings.teammate-workload.channel-limit.email-tooltip'),
        width: '10%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.messenger-channel-limit'),
        valuePath: 'messengerChannelLimit',
        isSortable: true,
        isVisible: this.appService.app.canUseChannelAssignmentLimits,
        tooltip: this.intl.t('settings.teammate-workload.channel-limit.messenger-tooltip'),
        width: '10%',
      },
      {
        label: this.intl.t(primaryLabel),
        valuePath: 'primaryInboxes',
        isSortable: true,
        width: '22%',
        tooltip: primaryTooltip,
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.secondary-inboxes'),
        valuePath: 'secondaryInboxes',
        isSortable: true,
        isVisible: true,
        tooltip: this.intl.t('settings.teammate-workload.secondary-inboxes.tooltip'),
        width: '22%',
      },
      {
        label: this.intl.t('settings.teammate-workload.columns.tags'),
        valuePath: 'tags',
        isSortable: true,
        isVisible: this.appService.app.canUseLbaTagMatch,
        tooltip: this.intl.t('settings.teammate-workload.tags.tooltip'),
        width: '22%',
      },
    ];
  }

  get lbaDescription() {
    let prefix = 'settings.teammate-workload.assignment-limit';

    if (
      !this.appService.app.canUseTicketsAssignmentSwitch &&
      this.appService.app.excludeTicketsBalancedAssignment
    ) {
      return `${prefix}.balanced-description-tickets-excluded`;
    }

    return `${prefix}.balanced-description`;
  }

  get teammates() {
    return this.args.teammates;
  }

  @cached
  get filteredTeammates() {
    let updatedTeammates = this.teammates;
    for (let [type, filter] of Object.entries(this.selectedFilters)) {
      updatedTeammates = this.processFilter(type, filter, updatedTeammates);
    }

    if (!isEqual(this.statusFilter, [NO_FILTER])) {
      updatedTeammates = this.processStatusFilter(updatedTeammates);
    }

    return isPresent(this.normalizedSearchTerm)
      ? this.searchTeammates(updatedTeammates)
      : updatedTeammates;
  }

  @cached
  get sortedTeammates() {
    let teammates = [...this.filteredTeammates];
    let { valuePath, direction } = this.sortState;
    let multiplier = direction === 'asc' ? 1 : -1;

    let countMap;
    if (valuePath === 'primaryInboxes' || valuePath === 'secondaryInboxes') {
      let inboxes = valuePath === 'primaryInboxes' ? this.args.teamInboxes : this.balancedInboxes;
      let memberKey = valuePath === 'primaryInboxes' ? 'primaryMembers' : 'secondaryMembers';
      countMap = new Map();
      for (let teammate of teammates) {
        let id = parseInt(teammate.id, 10);
        countMap.set(teammate.id, inboxes.filter((inbox) => inbox[memberKey]?.includes(id)).length);
      }
    }

    teammates.sort((a, b) => {
      let result = 0;
      switch (valuePath) {
        case 'name':
          result = (a.name || '').localeCompare(b.name || '');
          break;
        case 'assignmentLimit':
          result =
            this.assignmentLimitForTeammate(ASSIGNMENT_LIMIT_FILTER_TYPE, a) -
            this.assignmentLimitForTeammate(ASSIGNMENT_LIMIT_FILTER_TYPE, b);
          break;
        case 'ticketAssignmentLimit':
          result =
            this.assignmentLimitForTeammate(TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE, a) -
            this.assignmentLimitForTeammate(TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE, b);
          break;
        case 'emailChannelLimit':
          result =
            this.channelLimitForTeammate('email', a) - this.channelLimitForTeammate('email', b);
          break;
        case 'messengerChannelLimit':
          result =
            this.channelLimitForTeammate('messenger', a) -
            this.channelLimitForTeammate('messenger', b);
          break;
        case 'primaryInboxes':
        case 'secondaryInboxes':
          result = countMap.get(a.id) - countMap.get(b.id);
          break;
        case 'tags':
          result = (a.taggings?.length ?? 0) - (b.taggings?.length ?? 0);
          break;
      }
      return result * multiplier;
    });

    return teammates;
  }

  get renderableData() {
    return this.sortedTeammates.slice(0, this.pageSize * (this.currentPage + 1));
  }

  get hasActiveFilters() {
    return (
      this.searchTerm.length > 0 ||
      !isEqual(this.selectedFilters, DEFAULT_FILTERS) ||
      !isEqual(this.statusFilter, [NO_FILTER])
    );
  }

  get lastPageHit() {
    return this.filteredTeammates.length <= this.pageSize * (this.currentPage + 1);
  }

  get sortedTeamInboxes() {
    return this.args.teamInboxes.sortBy('name');
  }

  @cached
  get balancedInboxes() {
    let teamSettings = this.store.peekAll('team-setting', {});

    return this.args.teamInboxes.filter((inbox) => {
      let teamSetting = teamSettings.find((teamSetting) => teamSetting.teamId === inbox.id);
      return teamSetting?.distributionMethod === 'load_balanced';
    });
  }

  get bulkUpdateAllowed() {
    return this.selectedTeammates.length <= this.args.bulkUpdateLimit;
  }

  get shouldShowEmptyState() {
    return this.renderableData.length === 0;
  }

  get tableModeIsExpanded() {
    return this.tableMode === 'EXPAND';
  }

  updateSearchTerm() {
    this.normalizedSearchTerm = this.searchTerm.toLowerCase();
  }

  @action
  onLoadMore() {
    this.currentPage++;
  }

  @action
  onSort(valuePath) {
    let direction = 'asc';
    if (this.sortState.valuePath === valuePath && this.sortState.direction === 'asc') {
      direction = 'desc';
    }
    this.sortState = { valuePath, direction };
    this._saveSortState();
  }

  _loadSortState() {
    try {
      let stored = localStorage.getItem(SORT_STORAGE_KEY);
      if (stored) {
        let parsed = JSON.parse(stored);
        if (parsed.valuePath && parsed.direction) {
          return parsed;
        }
      }
    } catch {
      // localStorage unavailable or corrupted — use default
    }
    return { valuePath: 'name', direction: 'asc' };
  }

  _saveSortState() {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(this.sortState));
    } catch {
      // localStorage unavailable — silently fail
    }
  }

  get _filterStorageKey() {
    return `${FILTER_STORAGE_KEY_PREFIX}:${this.appService.app.id}`;
  }

  _loadFilterState() {
    let stored = storage.get(this._filterStorageKey);
    if (stored && this._isValidFilterState(stored)) {
      return stored;
    }
    return DEFAULT_FILTERS;
  }

  _saveFilterState() {
    storage.set(this._filterStorageKey, this.selectedFilters);
  }

  _clearFilterState() {
    storage.remove(this._filterStorageKey);
  }

  _isValidFilterState(state) {
    let validComparators = Object.values(ASSIGNMENT_LIMIT_COMPARATORS);
    let expectedTypes = [
      ASSIGNMENT_LIMIT_FILTER_TYPE,
      TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE,
      TEAM_FILTER_TYPE,
      ROLE_FILTER_TYPE,
    ];

    for (let type of expectedTypes) {
      let filter = state[type];
      if (!filter || filter.type !== type) {
        return false;
      }
    }

    if (!validComparators.includes(state[ASSIGNMENT_LIMIT_FILTER_TYPE].comparator)) {
      return false;
    }
    if (!validComparators.includes(state[TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE].comparator)) {
      return false;
    }
    if (!Array.isArray(state[TEAM_FILTER_TYPE].value)) {
      return false;
    }
    if (!Array.isArray(state[ROLE_FILTER_TYPE].value)) {
      return false;
    }

    return true;
  }

  @action
  onChangeSearchTerm() {
    debounce(this, this.updateSearchTerm, ENV.APP._250MS);
  }

  @action
  onChangeTableMode() {
    if (this.tableMode === 'SHRINK') {
      this.tableMode = 'EXPAND';
    } else {
      this.tableMode = 'SHRINK';
    }
  }

  searchTeammates(teammates) {
    return teammates.filter((teammate) => {
      return (
        teammate.name.toLowerCase().includes(this.normalizedSearchTerm) ||
        teammate.email.toLowerCase().includes(this.normalizedSearchTerm)
      );
    });
  }

  processFilter(type, filter, updatedTeammates) {
    switch (type) {
      case ASSIGNMENT_LIMIT_FILTER_TYPE:
      case TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE:
        return this.processAssignmentLimitFilter(type, filter, updatedTeammates);
      case TEAM_FILTER_TYPE:
        return this.processTeamFilter(filter, updatedTeammates);
      case ROLE_FILTER_TYPE:
        return this.processRoleFilter(filter, updatedTeammates);
    }
  }

  processAssignmentLimitFilter(type, filter, updatedTeammates) {
    switch (filter.comparator) {
      case ASSIGNMENT_LIMIT_COMPARATORS.greaterThan:
        return updatedTeammates.filter(
          (teammate) => this.assignmentLimitForTeammate(type, teammate) > filter.value,
        );
      case ASSIGNMENT_LIMIT_COMPARATORS.lessThan:
        return updatedTeammates.filter(
          (teammate) => this.assignmentLimitForTeammate(type, teammate) < filter.value,
        );
      case ASSIGNMENT_LIMIT_COMPARATORS.is:
        return updatedTeammates.filter(
          (teammate) => this.assignmentLimitForTeammate(type, teammate) === filter.value,
        );
      case ASSIGNMENT_LIMIT_COMPARATORS.isNot:
        return updatedTeammates.filter(
          (teammate) => this.assignmentLimitForTeammate(type, teammate) !== filter.value,
        );
      case ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue:
        return updatedTeammates;
    }
  }

  processTeamFilter(filter, updatedTeammates) {
    if (filter.value.length === 0) {
      return updatedTeammates;
    }

    return updatedTeammates.filter((teammate) => filter.value.includes(parseInt(teammate.id, 10)));
  }

  processRoleFilter(filter, updatedTeammates) {
    if (filter.value.length === 0) {
      return updatedTeammates;
    }

    return updatedTeammates.filter((teammate) => {
      // stub the role Id to -1 when no role is present. Allows filtering for no role.
      let roleId = parseInt(teammate.currentAppPermissions.role?.id, 10) || -1;
      return filter.value.includes(roleId);
    });
  }

  processStatusFilter(teammates) {
    let statusKeys = Object.fromEntries(Object.keys(STATUS_OBJECT).map((k) => [k, k]));

    return teammates.filter((teammate) =>
      this.statusFilter.some((filter) => {
        if (filter === statusKeys.active) {
          return !teammate.away_mode_enabled && !teammate.reassign_conversations;
        }
        if (filter === statusKeys.away) {
          return teammate.away_mode_enabled && !teammate.reassign_conversations;
        }
        if (filter === statusKeys.reassign) {
          return teammate.away_mode_enabled && teammate.reassign_conversations;
        }
        return false;
      }),
    );
  }

  channelLimitForTeammate(channelType, teammate) {
    return (
      teammate.workload_management_settings?.channel_limits?.[channelType] ??
      this.args.workspaceSettings?.channelLimits?.[channelType] ??
      this.args.workspaceSettings?.capacityLimit ??
      0
    );
  }

  assignmentLimitForTeammate(type, teammate) {
    switch (type) {
      case ASSIGNMENT_LIMIT_FILTER_TYPE:
        return (
          teammate.workload_management_settings?.assignment_limit ??
          this.args.workspaceSettings?.capacityLimit ??
          0
        );
      case TICKET_ASSIGNMENT_LIMIT_FILTER_TYPE:
        return (
          teammate.workload_management_settings?.ticket_assignment_limit ??
          this.args.workspaceSettings?.ticketAssignmentLimit ??
          0
        );
    }
  }

  @action
  handleSelect(rowId) {
    let teammates;
    if (this.selectedTeammates.includes(rowId)) {
      teammates = this.selectedTeammates.without(rowId);
    } else {
      teammates = [...this.selectedTeammates, rowId];
    }
    this.selectedTeammates = teammates;
    this.filteredTeammates.forEach((teammate, index) => {
      teammate.isSelected = this.selectedTeammates.includes(teammate.id);
    });
  }

  @action
  onBulkSelect() {
    let hasAllSelected = this.filteredTeammates.every((teammate) =>
      this.selectedTeammates.includes(teammate.id),
    );
    if (hasAllSelected) {
      this.resetBulkSelect();
    } else if (this.selectedTeammates.length >= 0) {
      this.selectedTeammates = this.filteredTeammates.map((teammate) => teammate.id);
      this.filteredTeammates.forEach((teammate, index) => {
        if (index < this.filteredTeammates.length) {
          teammate.isSelected = true;
        }
      });
    }
  }

  @action
  resetBulkSelect() {
    this.selectedTeammates = [];
    this.filteredTeammates.forEach((teammate, index) => {
      if (index < this.filteredTeammates.length) {
        teammate.isSelected = false;
      }
    });
  }

  @action
  onChangeFilter(filter) {
    this.selectedFilters = {
      ...this.selectedFilters,
      [filter.type]: filter,
    };
    this._saveFilterState();
  }

  @action
  onChangeStatusFilter(_type, values) {
    this.statusFilter = values;
    this.currentPage = 0;
  }

  @action
  clearFilters() {
    this.pageSize = DEFAULT_PAGE_SIZE;
    this.currentPage = 0;
    this.selectedFilters = DEFAULT_FILTERS;
    this.searchTerm = '';
    this.normalizedSearchTerm = '';
    this.statusFilter = [NO_FILTER];
    this._clearFilterState();
  }

  @action
  exportTeammates() {
    let exporter = new TeammatesCsvExporter(this.appService.app, this.sortedTeammates, {
      workspaceAssignmentLimit: this.args.workspaceSettings?.capacityLimit ?? 0,
      workspaceTicketAssignmentLimit: this.args.workspaceSettings?.ticketAssignmentLimit ?? 0,
      teamInboxes: this.sortedTeamInboxes,
      teamSettings: this.store
        .peekAll('team-setting', {})
        .filter((teamSetting) => this.sortedTeamInboxes.mapBy('id').includes(teamSetting.teamId)),
      intl: this.intl,
    });

    this.csv.export(exporter.data, {
      fileName: exporter.filename,
      withSeparator: false,
    });
  }
}
